/**
 * Morph Tracing — eval selection (which Reflexes to run on a trace).
 *
 * The public API is `evals` (see {@link EvalSelection}): `{ user, assistant }` choosing which role
 * each model classifies. We deliberately keep the public surface to plain nouns and hide the wire
 * detail: the selection is serialized onto a single
 * `morph.reflex.run` attribute on the interaction's workflow span (NOT an association property,
 * so it is not copied onto child LLM spans), and the per-role choice maps to the backend's
 * transform ids. Morph's ingest reads that attribute and runs the classifications async; results
 * land in the traces dashboard already labeled.
 */
import type { EvalSelection } from './types.js';

/** Raw span attribute key carrying the serialized selection (internal wire detail). */
export const REFLEX_RUN_ATTRIBUTE = 'morph.reflex.run';

// Public role → backend transform id. `user` classifies the user's message, `assistant` the
// agent's output. (Whole-conversation evals are not exposed yet.)
const USER_TRANSFORM = 'user_message';
const ASSISTANT_TRANSFORM = 'assistant_message';

/** One serialized entry on the wire: the model alias and the transform to run it on. */
export interface EvalRunEntry {
  model: string;
  transform: string;
}

/**
 * Normalize a public `evals` selection into deduped `{ model, transform }` wire entries.
 * `user` models run on the user turn, `assistant` models on the agent turn. Drops blank models;
 * preserves first-seen order. Exported for tests and so callers can see exactly what runs.
 */
export function normalizeEvals(evals: EvalSelection | undefined): EvalRunEntry[] {
  if (!evals) return [];
  const out: EvalRunEntry[] = [];
  const seen = new Set<string>();
  const add = (models: string[] | undefined, transform: string): void => {
    // Guard against a non-array (e.g. an untyped JS caller passing { user: "jailbreak" }) — iterating
    // a string would emit one bogus entry per character. Types forbid this; this is runtime defense.
    if (!Array.isArray(models)) return;
    for (const raw of models) {
      const model = typeof raw === 'string' ? raw.trim() : '';
      if (!model) continue;
      const key = `${model} ${transform}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ model, transform });
    }
  };
  add(evals.user, USER_TRANSFORM);
  add(evals.assistant, ASSISTANT_TRANSFORM);
  return out;
}

/**
 * Serialize an `evals` selection to the `morph.reflex.run` attribute string, or `null` when
 * there is nothing to run (so the attribute is simply omitted and no classification is enqueued).
 */
export function serializeEvals(evals: EvalSelection | undefined): string | null {
  const entries = normalizeEvals(evals);
  return entries.length ? JSON.stringify(entries) : null;
}
